import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Alert, LinearProgress,
  Stack, Chip, TextField, Accordion, AccordionSummary, AccordionDetails,
  Divider, CircularProgress,
} from '@mui/material';
import {
  Psychology as PromptIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { promptConfigurationsApi } from '../api/services';
import type { PromptConfiguration } from '../types/models';

interface FunctionGroup {
  functionKey: string;
  active: PromptConfiguration;
  history: PromptConfiguration[];
  editText: string;
  notes: string;
  saving: boolean;
  historyLoaded: boolean;
  historyLoading: boolean;
}

export default function PromptManagementPage() {
  const [groups, setGroups] = useState<FunctionGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const configs = await promptConfigurationsApi.getAll();
        // Group by functionKey, find active version per key
        const byKey: Record<string, PromptConfiguration[]> = {};
        configs.forEach(c => {
          if (!byKey[c.functionKey]) byKey[c.functionKey] = [];
          byKey[c.functionKey].push(c);
        });

        const grouped = Object.entries(byKey).map(([key, versions]) => {
          const active = versions.find(v => v.isActive) || versions.reduce((a, b) => a.version > b.version ? a : b);
          return {
            functionKey: key,
            active,
            history: [],
            editText: active.systemPrompt,
            notes: '',
            saving: false,
            historyLoaded: false,
            historyLoading: false,
          };
        });

        setGroups(grouped.sort((a, b) => a.functionKey.localeCompare(b.functionKey)));
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to load prompt configurations');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleLoadHistory = async (index: number) => {
    const group = groups[index];
    if (group.historyLoaded) return;

    setGroups(prev => prev.map((g, i) => i === index ? { ...g, historyLoading: true } : g));
    try {
      const history = await promptConfigurationsApi.getHistory(group.functionKey);
      setGroups(prev => prev.map((g, i) => i === index
        ? { ...g, history: history.sort((a, b) => b.version - a.version), historyLoaded: true, historyLoading: false }
        : g
      ));
    } catch (err) {
      setGroups(prev => prev.map((g, i) => i === index ? { ...g, historyLoading: false } : g));
    }
  };

  const handleSaveNewVersion = async (index: number) => {
    const group = groups[index];
    if (group.editText === group.active.systemPrompt) {
      setError('No changes to save');
      return;
    }

    setGroups(prev => prev.map((g, i) => i === index ? { ...g, saving: true } : g));
    setError(null);
    setSuccess(null);

    try {
      const newConfig = await promptConfigurationsApi.create({
        functionKey: group.functionKey,
        systemPrompt: group.editText,
        userPromptTemplate: group.active.userPromptTemplate,
        notes: group.notes || null,
      });

      setGroups(prev => prev.map((g, i) => i === index
        ? {
            ...g,
            active: newConfig,
            editText: newConfig.systemPrompt,
            notes: '',
            saving: false,
            historyLoaded: false,
            history: [],
          }
        : g
      ));
      setSuccess(`New version ${newConfig.version} saved for ${group.functionKey}`);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save');
      setGroups(prev => prev.map((g, i) => i === index ? { ...g, saving: false } : g));
    }
  };

  const updateGroup = (index: number, updates: Partial<FunctionGroup>) => {
    setGroups(prev => prev.map((g, i) => i === index ? { ...g, ...updates } : g));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <PromptIcon sx={{ color: 'primary.main' }} />
        <Typography variant="h4">Prompt Management</Typography>
      </Box>

      <Alert severity="warning" sx={{ mb: 3 }}>
        Changes to prompts affect AI planning quality. Test carefully.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {!loading && groups.length === 0 && (
        <Card sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>No prompt configurations found</Typography>
          <Typography color="text.secondary">
            Prompt configurations will appear here once the backend seeds them.
          </Typography>
        </Card>
      )}

      <Stack spacing={2}>
        {groups.map((group, index) => (
          <Accordion key={group.functionKey} sx={{ bgcolor: 'background.paper' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
                  {group.functionKey}
                </Typography>
                <Chip label={`v${group.active.version}`} size="small" color="primary" variant="outlined" />
                <Typography variant="caption" color="text.secondary">
                  Updated {new Date(group.active.updatedAt).toLocaleDateString()}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Typography variant="subtitle2" color="text.secondary">System Prompt</Typography>
                <TextField
                  multiline
                  minRows={6}
                  maxRows={20}
                  fullWidth
                  value={group.editText}
                  onChange={e => updateGroup(index, { editText: e.target.value })}
                  sx={{ '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                />

                <TextField
                  label="Notes (optional)"
                  fullWidth
                  size="small"
                  value={group.notes}
                  onChange={e => updateGroup(index, { notes: e.target.value })}
                  placeholder="Describe what changed and why"
                />

                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={group.saving ? <CircularProgress size={16} /> : <SaveIcon />}
                    onClick={() => handleSaveNewVersion(index)}
                    disabled={group.saving || group.editText === group.active.systemPrompt}
                  >
                    Save as New Version
                  </Button>
                </Stack>

                <Divider />

                {/* Version History */}
                <Accordion
                  sx={{ bgcolor: 'rgba(255,255,255,0.02)' }}
                  onChange={(_, expanded) => { if (expanded) handleLoadHistory(index); }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <HistoryIcon fontSize="small" />
                      <Typography variant="subtitle2">Version History</Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    {group.historyLoading && <LinearProgress />}
                    {group.historyLoaded && group.history.length === 0 && (
                      <Typography color="text.secondary" variant="body2">No version history found</Typography>
                    )}
                    <Stack spacing={1}>
                      {group.history.map(h => (
                        <Box
                          key={h.id}
                          sx={{
                            p: 1.5, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)',
                            border: h.isActive ? 2 : 0, borderColor: 'primary.main',
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip label={`v${h.version}`} size="small" color={h.isActive ? 'primary' : 'default'} />
                              {h.isActive && <Chip label="Active" size="small" color="success" />}
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(h.createdAt).toLocaleString()}
                            </Typography>
                          </Box>
                          {h.notes && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {h.notes}
                            </Typography>
                          )}
                          <Typography
                            variant="body2"
                            sx={{ mt: 1, fontFamily: 'monospace', fontSize: '0.8rem', maxHeight: 100, overflow: 'auto', whiteSpace: 'pre-wrap' }}
                          >
                            {h.systemPrompt.substring(0, 300)}{h.systemPrompt.length > 300 ? '...' : ''}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
    </Box>
  );
}
